// ═══════════════════════════════════════════════════════════════════════════════
// Register Page — multi-step registration wizard (email / Google, variable steps)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  USER_ROLE,
  PAIRING_CODE_LENGTH,
  INVITATION_CODE_LENGTH,
  INVITATION_CODE_REDEEM_REASON,
  INVITATION_CODE_TYPES,
  INVITATION_CODE_VALIDATE_REASON,
  registerInputSchema,
  emailSchema,
  nicknameSchema,
  resolveLegalDocumentLocale,
  type UserProfile,
  type UserPublic,
  type ConsentPublicVersionResponse,
  type LegalDocumentPublic,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  DEFAULT_USAGE_REGION,
  type UsageRegion,
  type RegistrationConfig,
  type InvitationCodeType,
  type InvitationCodeValidateResult,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { api, legalDocumentApi } from '../../lib/api';
import { getRedirectAfterAuth } from '../../lib/authRedirect';
import { AuthLayout } from './AuthLayout';
import { PasswordField } from '../../components/auth/PasswordField';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { AppButton } from '../../components/ui/AppButton';
import { UsageRegionSelect } from '../../components/settings/UsageRegionSelect';
import { usageRegionLabelKey } from '../../i18n/usageRegionMessages';
import { getConsentChromeContent } from '../../i18n/consentChrome';
import {
  clearPendingGoogleRegistration,
  readPendingGoogleRegistration,
  hasGoogleClientId,
  startGoogleRedirectAuth,
} from '../../lib/googleIdentity';
import {
  clearPendingPairInvite,
  hasPendingPairInvite,
  readPendingPairInvite,
} from '../../lib/pairInviteSession';
import {
  clearPendingInviteCode,
  readPendingInviteCode,
} from '../../lib/inviteCodeSession';
import {
  executePairInviteJoin,
  navigateToPairInviteExpired,
} from '../../lib/completePendingPairInvite';

type RegisterWizardStepId =
  | 'accountMethod'
  | 'role'
  | 'nickname'
  | 'password'
  | 'pairing'
  | 'pics'
  | 'review'
  | 'complete';

type EmailAvailabilityData = {
  valid: boolean;
  available: boolean | null;
};

type JoinPairingResponse = {
  pairingId: string;
  employer: UserPublic;
};

const renderLegalMarkdown = (content: string) =>
  content.split('\n').map((line, index) => {
    const key = `${index}-${line.slice(0, 12)}`;
    if (line.trim() === '') {
      return <div key={key} className="h-3" aria-hidden />;
    }
    if (line.startsWith('### ')) {
      return (
        <h3 key={key} className="mb-2 mt-4 text-sm font-bold text-gray-900 first:mt-0">
          {line.replace(/^###\s+/, '')}
        </h3>
      );
    }
    return (
      <p key={key} className="whitespace-pre-line text-xs leading-relaxed text-gray-700 sm:text-sm">
        {line}
      </p>
    );
  });

const toLegalLocale = resolveLegalDocumentLocale;

const recoverStepIndex = (
  previousSteps: RegisterWizardStepId[],
  nextSteps: RegisterWizardStepId[],
  previousIndex: number
): number => {
  const oldId = previousSteps[previousIndex];
  if (oldId && nextSteps.includes(oldId)) {
    return nextSteps.indexOf(oldId);
  }
  for (let j = previousIndex + 1; j < previousSteps.length; j += 1) {
    const candidate = previousSteps[j];
    const idx = nextSteps.indexOf(candidate);
    if (idx >= 0) {
      return idx;
    }
  }
  for (let j = previousIndex - 1; j >= 0; j -= 1) {
    const candidate = previousSteps[j];
    const idx = nextSteps.indexOf(candidate);
    if (idx >= 0) {
      return idx;
    }
  }
  return Math.max(0, nextSteps.length - 1);
};

const buildWizardSteps = (args: {
  skipAccountMethod: boolean;
  authMethod: 'email' | 'google';
  role: '' | 'employer' | 'helper';
  pairInviteMode: boolean;
}): RegisterWizardStepId[] => {
  const steps: RegisterWizardStepId[] = [];
  if (!args.skipAccountMethod) {
    steps.push('accountMethod');
  }
  if (!args.pairInviteMode) {
    steps.push('role');
  }
  steps.push('nickname');
  if (args.authMethod === 'email') {
    steps.push('password');
  }
  if (args.role === USER_ROLE.HELPER && !args.pairInviteMode) {
    steps.push('pairing');
  }
  steps.push('pics', 'review', 'complete');
  return steps;
};

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const { register, registerWithGoogle, refreshUser, isAuthenticated, user, isLoading: authBooting } =
    useAuth();

  const initialPairInvite = readPendingPairInvite();

  const [authMethod, setAuthMethod] = useState<'email' | 'google'>('email');
  const [googlePendingToken, setGooglePendingToken] = useState<string | null>(null);
  const [skipAccountMethod, setSkipAccountMethod] = useState(false);

  const [role, setRole] = useState<'' | 'employer' | 'helper'>(
    initialPairInvite ? USER_ROLE.HELPER : ''
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [usageRegion, setUsageRegion] = useState<UsageRegion>(DEFAULT_USAGE_REGION);
  const [pairingCode, setPairingCode] = useState(initialPairInvite?.code ?? '');
  const initialInviteCode = readPendingInviteCode();
  const [invitationCode, setInvitationCode] = useState(initialInviteCode?.code ?? '');
  const [invitationCodeLocked, setInvitationCodeLocked] = useState(
    initialInviteCode?.locked === true && Boolean(initialInviteCode.code),
  );
  const [inviteCodeInvalidHint, setInviteCodeInvalidHint] = useState(
    initialInviteCode?.showInvalidHint === true,
  );
  const [invitationCodeRequired, setInvitationCodeRequired] = useState(false);
  const [invitationCodeValid, setInvitationCodeValid] = useState<boolean | null>(null);
  const [invitationCodeReason, setInvitationCodeReason] = useState<string | null>(null);
  const [invitationCodeType, setInvitationCodeType] = useState<InvitationCodeType | null>(null);
  const [invitationCodeDurationDays, setInvitationCodeDurationDays] = useState<number | null>(null);
  const [invitationCodeCheckLoading, setInvitationCodeCheckLoading] = useState(false);

  const [stepIndex, setStepIndex] = useState(0);
  const prevStepsKeyRef = useRef('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRoleConfirmOpen, setIsRoleConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [consentVersion, setConsentVersion] = useState('');
  const [picsDocument, setPicsDocument] = useState<LegalDocumentPublic | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isFirstLanguageEffect = useRef(true);

  const [emailAvailability, setEmailAvailability] = useState<boolean | null>(null);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [registeredUser, setRegisteredUser] = useState<UserProfile | null>(null);
  const [pairInviteMode, setPairInviteMode] = useState(Boolean(initialPairInvite));
  const [pairInviteEmployerNickname, setPairInviteEmployerNickname] = useState(
    initialPairInvite?.employerNickname ?? ''
  );

  const steps = useMemo(
    () =>
      buildWizardSteps({
        skipAccountMethod,
        authMethod,
        role,
        pairInviteMode,
      }),
    [skipAccountMethod, authMethod, role, pairInviteMode]
  );

  const stepsKey = steps.join('|');

  useEffect(() => {
    const prevKey = prevStepsKeyRef.current;
    if (prevKey === stepsKey) {
      return;
    }
    const prevSteps =
      prevKey === '' ? ([] as RegisterWizardStepId[]) : (prevKey.split('|') as RegisterWizardStepId[]);
    prevStepsKeyRef.current = stepsKey;
    if (prevSteps.length === 0) {
      return;
    }
    setStepIndex((prevIdx) => recoverStepIndex(prevSteps, steps, prevIdx));
  }, [stepsKey, steps]);

  useEffect(() => {
    if (authBooting || !isAuthenticated || !user) return;
    if (registeredUser) return;
    if (isSubmitting) return;
    navigate(getRedirectAfterAuth(user), { replace: true });
  }, [authBooting, isAuthenticated, user, navigate, registeredUser, isSubmitting]);

  useEffect(() => {
    const googleRegister = searchParams.get('googleRegister');
    if (googleRegister !== '1') {
      return;
    }
    if (isAuthenticated && user && !registeredUser) {
      clearPendingGoogleRegistration();
      setSearchParams({}, { replace: true });
      return;
    }
    const pending = readPendingGoogleRegistration();
    if (!pending?.idToken) {
      setSearchParams({}, { replace: true });
      return;
    }
    setGooglePendingToken(pending.idToken);
    setEmail(pending.email);
    setAuthMethod('google');
    setSkipAccountMethod(true);
    setPassword('');
    setConfirmPassword('');
    if (pending.pairInviteMode) {
      setPairInviteMode(true);
      setRole(USER_ROLE.HELPER);
      if (pending.pairingCode) {
        setPairingCode(pending.pairingCode);
      }
      const storedInvite = readPendingPairInvite();
      if (storedInvite?.employerNickname) {
        setPairInviteEmployerNickname(storedInvite.employerNickname);
      }
    }
  }, [searchParams, setSearchParams, isAuthenticated, user, registeredUser]);

  useEffect(() => {
    const loadVersion = async () => {
      try {
        const documentResponse = await legalDocumentApi.getActive('pics', toLegalLocale(language));
        if (documentResponse.success && documentResponse.data) {
          setPicsDocument(documentResponse.data);
        }
      } catch {
        // Fallback to bundled PICS content if DB content is unavailable.
      }

      try {
        const response = await api.get<ConsentPublicVersionResponse>('/consent/public-version');
        if (response.success && response.data?.currentVersion) {
          setConsentVersion(response.data.currentVersion);
        }
      } catch {
        setConsentVersion('v1');
      }
    };
    void loadVersion();
  }, [language]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await api.get<RegistrationConfig>('/auth/registration-config');
        if (response.success && response.data) {
          setInvitationCodeRequired(response.data.invitationCodeRequired);
        }
      } catch {
        setInvitationCodeRequired(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (isFirstLanguageEffect.current) {
      isFirstLanguageEffect.current = false;
      return;
    }
    setAgreed(false);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setHasScrolledToBottom(false);
  }, [language]);

  useEffect(() => {
    if (authMethod !== 'email') {
      setEmailAvailability(null);
      setEmailCheckLoading(false);
      return;
    }
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailAvailability(null);
      setEmailCheckLoading(false);
      return;
    }
    const handle = setTimeout(() => {
      void (async () => {
        setEmailCheckLoading(true);
        try {
          const response = await api.get<EmailAvailabilityData>(
            `/auth/email-availability?email=${encodeURIComponent(parsed.data)}`
          );
          if (response.success && response.data?.valid) {
            setEmailAvailability(response.data.available ?? null);
          } else {
            setEmailAvailability(null);
          }
        } catch {
          setEmailAvailability(null);
        } finally {
          setEmailCheckLoading(false);
        }
      })();
    }, 450);
    return () => clearTimeout(handle);
  }, [email, authMethod]);

  useEffect(() => {
    if (role !== USER_ROLE.EMPLOYER) {
      setInvitationCodeValid(null);
      setInvitationCodeReason(null);
      setInvitationCodeType(null);
      setInvitationCodeDurationDays(null);
      setInvitationCodeCheckLoading(false);
      return;
    }
    if (invitationCode.length === 0) {
      setInvitationCodeValid(null);
      setInvitationCodeReason(null);
      setInvitationCodeType(null);
      setInvitationCodeDurationDays(null);
      setInvitationCodeCheckLoading(false);
      return;
    }
    if (invitationCode.length < INVITATION_CODE_LENGTH) {
      setInvitationCodeValid(false);
      setInvitationCodeReason(INVITATION_CODE_VALIDATE_REASON.FORMAT_INVALID);
      setInvitationCodeType(null);
      setInvitationCodeDurationDays(null);
      setInvitationCodeCheckLoading(false);
      return;
    }
    const handle = setTimeout(() => {
      void (async () => {
        setInvitationCodeCheckLoading(true);
        try {
          const response = await api.post<InvitationCodeValidateResult>(
            '/auth/invitation-code/validate',
            { code: invitationCode },
          );
          if (response.success && response.data) {
            setInvitationCodeValid(response.data.valid);
            if (response.data.valid) {
              setInvitationCodeReason(null);
              setInvitationCodeType(response.data.codeType);
              setInvitationCodeDurationDays(response.data.levelDurationDays);
            } else {
              setInvitationCodeReason(response.data.reason ?? null);
              setInvitationCodeType(null);
              setInvitationCodeDurationDays(null);
            }
          } else {
            setInvitationCodeValid(false);
            setInvitationCodeReason(INVITATION_CODE_VALIDATE_REASON.NOT_FOUND);
            setInvitationCodeType(null);
            setInvitationCodeDurationDays(null);
          }
        } catch {
          setInvitationCodeValid(false);
          setInvitationCodeReason(INVITATION_CODE_VALIDATE_REASON.NOT_FOUND);
          setInvitationCodeType(null);
          setInvitationCodeDurationDays(null);
        } finally {
          setInvitationCodeCheckLoading(false);
        }
      })();
    }, 450);
    return () => clearTimeout(handle);
  }, [invitationCode, role]);

  const invitationCodeErrorMessage = useCallback(
    (reason: string | null): string => {
      if (!reason) {
        return t('auth.invitationCodeInvalid');
      }
      const keyByReason: Record<string, string> = {
        [INVITATION_CODE_VALIDATE_REASON.FORMAT_INVALID]: 'auth.invitationCodeErrorFormat',
        [INVITATION_CODE_VALIDATE_REASON.NOT_FOUND]: 'auth.invitationCodeInvalid',
        [INVITATION_CODE_VALIDATE_REASON.NOT_YET_ACTIVE]: 'auth.invitationCodeErrorNotYetActive',
        [INVITATION_CODE_VALIDATE_REASON.EXPIRED]: 'auth.invitationCodeErrorExpired',
        [INVITATION_CODE_VALIDATE_REASON.DISABLED]: 'auth.invitationCodeErrorDisabled',
        [INVITATION_CODE_VALIDATE_REASON.MAX_USES_REACHED]: 'auth.invitationCodeErrorMaxUses',
        [INVITATION_CODE_REDEEM_REASON.INVITATION_CODE_AFTER_REGISTRATION_ONLY]:
          'auth.invitationCodeErrorAfterRegistrationOnly',
      };
      const key = keyByReason[reason] ?? 'auth.invitationCodeInvalid';
      return t(key);
    },
    [t],
  );

  const currentStepId = steps[stepIndex] ?? 'accountMethod';
  /** Google 路線省略步驟 1（OAuth 視為第 1 步），畫面上「選擇身份」起為第 2 步 */
  const isGoogleImplicitStep1 = skipAccountMethod && authMethod === 'google';
  const totalStepsDisplay = isGoogleImplicitStep1 ? steps.length + 1 : steps.length;
  const displayStepNumber = isGoogleImplicitStep1 ? stepIndex + 2 : stepIndex + 1;
  const progressRatio = totalStepsDisplay > 0 ? displayStepNumber / totalStepsDisplay : 0;

  const handleSelectEmployer = useCallback(() => {
    setRole(USER_ROLE.EMPLOYER);
    setPairingCode('');
    setError('');
  }, []);

  const handleSelectHelper = useCallback(() => {
    setRole(USER_ROLE.HELPER);
    setInvitationCode('');
    setInvitationCodeValid(null);
    setInvitationCodeReason(null);
    setError('');
  }, []);

  const handleInvitationCodeChange = useCallback(
    (value: string) => {
      if (invitationCodeLocked) {
        return;
      }
      const next = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, INVITATION_CODE_LENGTH);
      setInvitationCode(next);
      if (inviteCodeInvalidHint) {
        setInviteCodeInvalidHint(false);
      }
    },
    [invitationCodeLocked, inviteCodeInvalidHint],
  );

  useEffect(() => {
    if (initialInviteCode?.showInvalidHint) {
      clearPendingInviteCode();
    }
  }, [initialInviteCode?.showInvalidHint]);

  const handlePairingCodeChange = useCallback((value: string) => {
    const next = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, PAIRING_CODE_LENGTH);
    setPairingCode(next);
  }, []);

  const handleScrollPics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (atBottom) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleToggleAgreed = useCallback(() => {
    if (!hasScrolledToBottom) {
      return;
    }
    setAgreed((prev) => !prev);
  }, [hasScrolledToBottom]);

  const resolvePairingCodeForJoin = useCallback((): string => {
    const trimmed = pairingCode.trim().toUpperCase();
    if (trimmed.length === PAIRING_CODE_LENGTH) {
      return trimmed;
    }
    const pending = readPendingPairInvite();
    return pending?.code?.trim().toUpperCase() ?? '';
  }, [pairingCode]);

  const tryJoinPairing = useCallback(
    async (
      code: string
    ): Promise<{ user: UserProfile; pairingId: string; employer: UserPublic }> => {
      const response = await api.post<JoinPairingResponse>('/pairing/join', { code });
      if (!response.success || !response.data) {
        throw new Error(response.error ?? t('pairing.join.errorGeneric'));
      }
      const refreshed = await refreshUser();
      if (!refreshed) {
        throw new Error(t('auth.errorGeneric'));
      }
      return {
        user: refreshed,
        pairingId: response.data.pairingId,
        employer: response.data.employer,
      };
    },
    [refreshUser, t]
  );

  const handlePairingJoinFailure = useCallback(
    (joinErr: unknown) => {
      if (pairInviteMode) {
        navigateToPairInviteExpired(navigate);
        return;
      }
      const msg =
        joinErr instanceof Error ? joinErr.message : t('pairing.join.errorGeneric');
      navigate('/pairing/join', { replace: true, state: { pairingError: msg } });
    },
    [navigate, pairInviteMode, t]
  );

  const completeHelperPairingAfterRegister = useCallback(
    async (registeredRole: 'employer' | 'helper'): Promise<'done' | 'navigated'> => {
      if (registeredRole !== USER_ROLE.HELPER) {
        return 'done';
      }
      const codeToJoin = resolvePairingCodeForJoin();
      if (codeToJoin.length !== PAIRING_CODE_LENGTH) {
        return 'done';
      }
      try {
        const joined = await tryJoinPairing(codeToJoin);
        setRegisteredUser(joined.user);
        if (pairInviteMode) {
          clearPendingPairInvite();
          navigate('/pairing/success', {
            replace: true,
            state: {
              pairingId: joined.pairingId,
              employer: joined.employer,
            },
          });
          return 'navigated';
        }
        return 'done';
      } catch (joinErr) {
        handlePairingJoinFailure(joinErr);
        return 'navigated';
      }
    },
    [
      resolvePairingCodeForJoin,
      tryJoinPairing,
      pairInviteMode,
      navigate,
      handlePairingJoinFailure,
    ]
  );

  const pics = getConsentChromeContent(language);
  const picsTitle = picsDocument?.title ?? pics.pageTitle;
  const picsVersion = consentVersion || picsDocument?.version || 'v1';

  const passwordLenOk = password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH;
  const passwordUpperOk = /[A-Z]/.test(password);
  const passwordLowerOk = /[a-z]/.test(password);
  const passwordDigitOk = /\d/.test(password);
  const passwordPolicyMet =
    passwordLenOk && passwordUpperOk && passwordLowerOk && passwordDigitOk;
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const handleConfirmRegister = useCallback(async () => {
    setError('');
    setIsRoleConfirmOpen(false);

    if (!role) {
      setError(t('auth.roleRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      if (googlePendingToken) {
        const nextUser = await registerWithGoogle(
          googlePendingToken,
          nickname.trim(),
          role,
          language,
          usageRegion,
          invitationCode,
        );
        clearPendingGoogleRegistration();
        clearPendingInviteCode();
        setInvitationCodeLocked(false);
        setRegisteredUser(nextUser);
        const pairingResult = await completeHelperPairingAfterRegister(role);
        if (pairingResult === 'navigated') {
          return;
        }
        setStepIndex(steps.indexOf('complete'));
        return;
      }
      const parsed = registerInputSchema.safeParse({
        email: email.trim(),
        password,
        confirmPassword,
        nickname: nickname.trim(),
        role,
        picsConsentAccepted: true,
        locale: language,
        usageRegion,
        invitationCode: invitationCode.trim() || undefined,
      });
      if (!parsed.success) {
        const msg = parsed.error.issues.map((i) => i.message).join(', ');
        setError(msg);
        return;
      }
      const {
        email: em,
        password: pw,
        confirmPassword: cpw,
        nickname: nn,
        role: r,
        usageRegion: ur,
        invitationCode: ic,
      } = parsed.data;
      const nextUser = await register(em, pw, cpw, nn, r, language, ur, ic);
      clearPendingInviteCode();
      setInvitationCodeLocked(false);
      setRegisteredUser(nextUser);

      const pairingResult = await completeHelperPairingAfterRegister(r);
      if (pairingResult === 'navigated') {
        return;
      }

      setStepIndex(steps.indexOf('complete'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    googlePendingToken,
    registerWithGoogle,
    nickname,
    usageRegion,
    role,
    language,
    invitationCode,
    completeHelperPairingAfterRegister,
    t,
    email,
    password,
    confirmPassword,
    register,
    steps,
  ]);

  const handleGoogleRegisterClick = useCallback(() => {
    setError('');
    try {
      startGoogleRedirectAuth({
        intent: 'register',
        fromPath: '/register',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    }
  }, [t]);

  const goToStepId = useCallback(
    (id: RegisterWizardStepId) => {
      const idx = steps.indexOf(id);
      if (idx >= 0) {
        setStepIndex(idx);
      }
      setError('');
    },
    [steps]
  );

  const goToAccountMethodStep = useCallback(() => {
    clearPendingGoogleRegistration();
    setGooglePendingToken(null);
    setSearchParams({}, { replace: true });
    setAuthMethod('email');
    setEmail('');
    setEmailAvailability(null);
    setEmailCheckLoading(false);
    setSkipAccountMethod(false);
    // Prevent auto-recover from pulling user back to prior step.
    prevStepsKeyRef.current = '';
    setStepIndex(0);
    setError('');
  }, [setSearchParams]);

  const handleWizardBack = useCallback(() => {
    setError('');
    if (stepIndex === 0 && skipAccountMethod && authMethod === 'google') {
      goToAccountMethodStep();
      return;
    }
    if (stepIndex <= 0) {
      navigate('/', { replace: true });
      return;
    }
    setStepIndex((i) => i - 1);
  }, [stepIndex, skipAccountMethod, authMethod, navigate, goToAccountMethodStep]);

  const handleContinueFromComplete = useCallback(async () => {
    if (!registeredUser) return;
    if (pairInviteMode && hasPendingPairInvite()) {
      const joined = await executePairInviteJoin(refreshUser, navigate);
      if (joined) {
        return;
      }
    }
    navigate(getRedirectAfterAuth(registeredUser), { replace: true });
  }, [navigate, registeredUser, pairInviteMode, refreshUser]);

  const validateCurrentStep = useCallback((): boolean => {
    setError('');
    if (currentStepId === 'accountMethod') {
      if (authMethod !== 'email') {
        setError(t('auth.errorGeneric'));
        return false;
      }
      const parsed = emailSchema.safeParse(email);
      if (!parsed.success) {
        setError(t('auth.emailInvalid'));
        return false;
      }
      if (emailCheckLoading) {
        setError(t('common.loading'));
        return false;
      }
      if (emailAvailability !== true) {
        setError(emailAvailability === false ? t('auth.emailTaken') : t('auth.emailInvalid'));
        return false;
      }
      return true;
    }
    if (currentStepId === 'role') {
      if (!role) {
        setError(t('auth.roleRequired'));
        return false;
      }
      return true;
    }
    if (currentStepId === 'nickname') {
      const nickParsed = nicknameSchema.safeParse(nickname);
      if (!nickParsed.success) {
        setError(nickParsed.error.issues.map((i) => i.message).join(', '));
        return false;
      }
      if (role === USER_ROLE.EMPLOYER) {
        const trimmedCode = invitationCode.trim();
        if (invitationCodeRequired && trimmedCode === '') {
          setError(t('auth.invitationCodeRequired'));
          return false;
        }
        if (trimmedCode !== '') {
          if (invitationCodeCheckLoading) {
            setError(t('common.loading'));
            return false;
          }
          if (invitationCode.length < INVITATION_CODE_LENGTH) {
            setError(invitationCodeErrorMessage(INVITATION_CODE_VALIDATE_REASON.FORMAT_INVALID));
            return false;
          }
          if (invitationCodeValid !== true) {
            setError(invitationCodeErrorMessage(invitationCodeReason));
            return false;
          }
        }
      }
      return true;
    }
    if (currentStepId === 'password') {
      if (!passwordPolicyMet) {
        setError(t('auth.passwordRulesHint'));
        return false;
      }
      if (!passwordsMatch) {
        setError(t('auth.passwordMismatch'));
        return false;
      }
      const parsed = registerInputSchema.safeParse({
        email: email.trim(),
        password,
        confirmPassword,
        nickname: nickname.trim(),
        role,
        picsConsentAccepted: true,
        locale: language,
        usageRegion,
        invitationCode: invitationCode.trim() || undefined,
      });
      if (!parsed.success) {
        setError(parsed.error.issues.map((i) => i.message).join(', '));
        return false;
      }
      return true;
    }
    if (currentStepId === 'pairing') {
      if (pairingCode.length > 0 && pairingCode.length < PAIRING_CODE_LENGTH) {
        setError(t('pairing.join.errorIncomplete', { length: PAIRING_CODE_LENGTH }));
        return false;
      }
      return true;
    }
    if (currentStepId === 'pics') {
      if (!hasScrolledToBottom) {
        setError(t('auth.registerPicsScrollRequired'));
        return false;
      }
      if (!agreed) {
        setError(t('auth.registerPicsAgreeRequired'));
        return false;
      }
      return true;
    }
    if (currentStepId === 'review' && role === USER_ROLE.EMPLOYER) {
      const trimmedCode = invitationCode.trim();
      if (invitationCodeRequired && trimmedCode === '') {
        setError(t('auth.invitationCodeRequired'));
        return false;
      }
      if (trimmedCode !== '') {
        if (invitationCodeCheckLoading) {
          setError(t('common.loading'));
          return false;
        }
        if (invitationCode.length < INVITATION_CODE_LENGTH) {
          setError(invitationCodeErrorMessage(INVITATION_CODE_VALIDATE_REASON.FORMAT_INVALID));
          return false;
        }
        if (invitationCodeValid !== true) {
          setError(invitationCodeErrorMessage(invitationCodeReason));
          return false;
        }
      }
    }
    return true;
  }, [
    currentStepId,
    authMethod,
    email,
    emailCheckLoading,
    emailAvailability,
    role,
    nickname,
    usageRegion,
    passwordPolicyMet,
    passwordsMatch,
    password,
    confirmPassword,
    language,
    pairingCode,
    invitationCode,
    invitationCodeRequired,
    invitationCodeCheckLoading,
    invitationCodeValid,
    invitationCodeReason,
    invitationCodeErrorMessage,
    hasScrolledToBottom,
    agreed,
    t,
  ]);

  const handleWizardNext = useCallback(() => {
    if (!validateCurrentStep()) {
      return;
    }
    if (stepIndex >= steps.length - 1) {
      return;
    }
    setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  }, [validateCurrentStep, stepIndex, steps.length]);

  const handleReviewSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      if (!validateCurrentStep()) {
        return;
      }
      if (!role) {
        setError(t('auth.roleRequired'));
        return;
      }
      if (pairInviteMode) {
        void handleConfirmRegister();
        return;
      }
      setIsRoleConfirmOpen(true);
    },
    [validateCurrentStep, role, t, pairInviteMode, handleConfirmRegister]
  );

  const layoutBack = stepIndex === 0 && currentStepId === 'accountMethod' ? '/' : undefined;
  const layoutOnBack =
    stepIndex > 0 || (stepIndex === 0 && skipAccountMethod && authMethod === 'google')
      ? handleWizardBack
      : undefined;

  if (authBooting) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-gray-50"
        role="status"
        aria-label={t('common.loading')}
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  const selectedRoleLabel =
    role === USER_ROLE.EMPLOYER
      ? t('role.employer')
      : role === USER_ROLE.HELPER
        ? t('role.helper')
        : '';

  const renderRoleIndicator = (isSelected: boolean) => (
    <span
      className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
        isSelected ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
      }`}
      aria-hidden
    >
      {isSelected ? (
        <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      ) : null}
    </span>
  );

  const renderProgress = () => (
    <div className="mb-6">
      <div className="mb-2 flex justify-center">
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">
          {t('auth.registerWizardStepBadge', { current: displayStepNumber, total: totalStepsDisplay })}
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-primary-100 dark:bg-primary-900/40"
        role="progressbar"
        aria-label={t('auth.registerWizardProgressAria')}
        aria-valuemin={1}
        aria-valuemax={totalStepsDisplay}
        aria-valuenow={displayStepNumber}
      >
        <div
          className="h-full rounded-full bg-primary-700 transition-all duration-300 dark:bg-primary-500"
          style={{ width: `${Math.min(100, Math.round(progressRatio * 100))}%` }}
        />
      </div>
    </div>
  );

  const renderPrimaryCta = ({
    children,
    ...rest
  }: {
    children: React.ReactNode;
  } & React.ComponentProps<typeof AppButton>) => (
    <AppButton variant="primary" size="pill" fullWidth className="shadow-sm" {...rest}>
      {children}
    </AppButton>
  );

  const renderAccountMethod = () => (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" id="register-heading">
          {t('auth.registerMethodTitle')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('auth.registerMethodSubtitle')}</p>
      </div>
      {hasGoogleClientId() ? (
        <button
          type="button"
          onClick={handleGoogleRegisterClick}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 text-base font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
          aria-label={t('auth.registerWithGoogle')}
        >
          <svg viewBox="0 0 18 18" className="h-5 w-5 shrink-0" aria-hidden>
            <path
              fill="#EA4335"
              d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.54C13.49.85 11.46 0 9 0 5.48 0 2.44 2.02.96 4.96l2.95 2.29C4.63 5.07 6.64 3.48 9 3.48Z"
            />
            <path
              fill="#4285F4"
              d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72l2.78 2.15c1.62-1.49 2.56-3.69 2.56-6.31Z"
            />
            <path
              fill="#FBBC05"
              d="M3.91 10.75a5.42 5.42 0 0 1 0-3.5L.96 4.96a9 9 0 0 0 0 8.08l2.95-2.29Z"
            />
            <path
              fill="#34A853"
              d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.78-2.15c-.77.52-1.75.83-3.18.83-2.36 0-4.37-1.6-5.09-3.75L.96 13.04C2.44 15.98 5.48 18 9 18Z"
            />
          </svg>
          {t('auth.registerWithGoogle')}
        </button>
      ) : null}

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        <span className="text-xs text-gray-500">{t('auth.registerDividerOrEmail')}</span>
        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
      </div>

      <div>
        <label htmlFor="register-email" className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
          {t('auth.emailLabel')}
        </label>
        <input
          id="register-email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setAuthMethod('email');
          }}
          className="mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm focus:border-primary-600 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          aria-invalid={emailAvailability === false}
          aria-describedby="register-email-hint register-email-status"
        />
        <p id="register-email-hint" className="mt-1 text-xs text-gray-500">
          {t('auth.registerEmailHelper')}
        </p>
        <p id="register-email-status" className="mt-1 text-xs" role="status">
          {emailCheckLoading ? (
            <span className="text-gray-500">{t('auth.emailChecking')}</span>
          ) : emailSchema.safeParse(email).success && emailAvailability === false ? (
            <span className="text-red-600">{t('auth.emailTaken')}</span>
          ) : null}
        </p>
      </div>

      <AppButton
        type="button"
        variant="primary"
        size="pill"
        fullWidth
        className="shadow-sm"
        onClick={handleWizardNext}
        disabled={
          authMethod !== 'email' ||
          emailCheckLoading ||
          emailAvailability !== true ||
          !emailSchema.safeParse(email).success
        }
      >
        {t('auth.registerContinueWithEmail')}
      </AppButton>
    </div>
  );

  const renderRole = () => (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth.registerStepRoleTitle')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('auth.registerStepRoleSubtitle')}</p>
      </div>
      <div className="space-y-3">
        <button
          type="button"
          onClick={handleSelectEmployer}
          className={`flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            role === USER_ROLE.EMPLOYER
              ? 'border-primary-600 bg-primary-50 dark:bg-primary-950/40 dark:border-primary-500'
              : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800'
          }`}
          aria-pressed={role === USER_ROLE.EMPLOYER}
        >
          {renderRoleIndicator(role === USER_ROLE.EMPLOYER)}
          <span>
            <span className="block text-base font-bold text-gray-900 dark:text-white">
              {t('auth.registerRoleEmployerTitle')}
            </span>
            <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">
              {t('auth.registerRoleEmployerDesc')}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={handleSelectHelper}
          className={`flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            role === USER_ROLE.HELPER
              ? 'border-primary-600 bg-primary-50 dark:bg-primary-950/40 dark:border-primary-500'
              : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800'
          }`}
          aria-pressed={role === USER_ROLE.HELPER}
        >
          {renderRoleIndicator(role === USER_ROLE.HELPER)}
          <span>
            <span className="block text-base font-bold text-gray-900 dark:text-white">
              {t('auth.registerRoleHelperTitle')}
            </span>
            <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">
              {t('auth.registerRoleHelperDesc')}
            </span>
          </span>
        </button>
      </div>
      <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/50 dark:text-amber-100">
        {t('auth.registerRoleImportantNotice')}
      </div>
      {renderPrimaryCta({
        type: 'button',
        onClick: handleWizardNext,
        disabled: !role,
        children: t('auth.registerContinue'),
      })}
    </div>
  );

  const renderNickname = () => (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth.registerStepNicknameTitle')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('auth.registerStepNicknameSubtitle')}</p>
      </div>
      <div>
        <label htmlFor="register-nickname" className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
          {t('auth.nicknameLabel')}
        </label>
        <input
          id="register-nickname"
          name="nickname"
          type="text"
          autoComplete="nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          className="mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm focus:border-primary-600 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        />
      </div>
      <UsageRegionSelect
        id="register-usage-region"
        value={usageRegion}
        onChange={setUsageRegion}
        label={t('settings.usageRegionLabel')}
      />
      {role === USER_ROLE.EMPLOYER ? (
        <div>
          <label
            htmlFor="register-invitation-code"
            className="block text-sm font-semibold text-gray-800 dark:text-gray-100"
          >
            {invitationCodeRequired
              ? t('auth.invitationCodeLabel')
              : t('auth.invitationCodeOptional')}
          </label>
          {inviteCodeInvalidHint ? (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300" role="alert">
              {t('auth.invitationCodeDeepLinkInvalid')}
            </p>
          ) : null}
          <input
            id="register-invitation-code"
            name="invitationCode"
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            maxLength={INVITATION_CODE_LENGTH}
            value={invitationCode}
            readOnly={invitationCodeLocked}
            onChange={(e) => handleInvitationCodeChange(e.target.value)}
            placeholder={t('auth.invitationCodePlaceholder')}
            aria-invalid={invitationCode.length > 0 && invitationCodeValid === false}
            aria-describedby="register-invitation-code-hint"
            aria-readonly={invitationCodeLocked}
            className={[
              'mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 font-mono tracking-widest text-gray-900 uppercase shadow-sm focus:border-primary-600 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white',
              invitationCodeLocked ? 'opacity-80' : '',
            ].join(' ')}
          />
          <p id="register-invitation-code-hint" className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {invitationCodeLocked
              ? t('auth.invitationCodeLockedHint')
              : t('auth.invitationCodeHint')}
          </p>
          {invitationCodeCheckLoading ? (
            <p className="mt-1 text-xs text-gray-500" role="status">
              {t('common.loading')}
            </p>
          ) : null}
          {!invitationCodeCheckLoading &&
          invitationCode.length > 0 &&
          invitationCodeValid === false ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
              {invitationCodeErrorMessage(invitationCodeReason)}
            </p>
          ) : null}
          {!invitationCodeCheckLoading &&
          invitationCode.length === INVITATION_CODE_LENGTH &&
          invitationCodeValid === true ? (
            <div className="mt-1 space-y-0.5" role="status">
              <p className="text-xs text-green-700 dark:text-green-400">
                {t('auth.invitationCodeValid')}
              </p>
              {invitationCodeType === INVITATION_CODE_TYPES.L1_TRIAL_EXTEND &&
              invitationCodeDurationDays != null &&
              invitationCodeDurationDays > 0 ? (
                <p className="text-xs text-green-700 dark:text-green-400">
                  {t('auth.invitationCodeBenefitL1Extend', { days: invitationCodeDurationDays })}
                </p>
              ) : null}
              {invitationCodeType === INVITATION_CODE_TYPES.L3_TRIAL_PERIOD &&
              invitationCodeDurationDays != null &&
              invitationCodeDurationDays > 0 ? (
                <p className="text-xs text-green-700 dark:text-green-400">
                  {t('auth.invitationCodeBenefitL3Trial', { days: invitationCodeDurationDays })}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {renderPrimaryCta({
        type: 'button',
        onClick: handleWizardNext,
        children: t('auth.registerContinue'),
      })}
    </div>
  );

  const renderPassword = () => (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth.registerStepPasswordTitle')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('auth.registerStepPasswordSubtitle')}</p>
      </div>
      <PasswordField
        id="register-password"
        name="password"
        label={t('auth.passwordLabel')}
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        disabled={isSubmitting}
        showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
        aria-describedby="register-password-policy"
      />
      <PasswordField
        id="register-confirm-password"
        name="confirmPassword"
        label={t('auth.confirmPasswordRegisterLabel')}
        value={confirmPassword}
        onChange={setConfirmPassword}
        autoComplete="new-password"
        disabled={isSubmitting}
        showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
        aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
      />
      {confirmPassword.length > 0 && !passwordsMatch ? (
        <p className="text-sm text-red-600" role="alert">
          {t('auth.passwordMismatch')}
        </p>
      ) : null}

      <div
        id="register-password-policy"
        className="rounded-2xl bg-gray-100 px-4 py-3 text-sm dark:bg-gray-800/80"
      >
        <p className="font-semibold text-gray-800 dark:text-gray-100">{t('auth.registerPasswordPolicyTitle')}</p>
        <ul className="mt-2 space-y-1">
          <li className={passwordLenOk ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500'}>
            {t('auth.registerPasswordPolicyLength')}
          </li>
          <li className={passwordUpperOk ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500'}>
            {t('auth.registerPasswordPolicyUpper')}
          </li>
          <li className={passwordLowerOk ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500'}>
            {t('auth.registerPasswordPolicyLower')}
          </li>
          <li className={passwordDigitOk ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500'}>
            {t('auth.registerPasswordPolicyDigit')}
          </li>
        </ul>
      </div>

      {renderPrimaryCta({
        type: 'button',
        onClick: handleWizardNext,
        disabled: !passwordPolicyMet || !passwordsMatch,
        children: t('auth.registerContinue'),
      })}
    </div>
  );

  const renderPairing = () => (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth.registerStepPairingTitle')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('auth.registerStepPairingSubtitle')}</p>
      </div>
      <div>
        <label htmlFor="register-pairing-code" className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
          {t('auth.pairingCodeOptional')}
        </label>
        <input
          id="register-pairing-code"
          name="pairingCode"
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={PAIRING_CODE_LENGTH}
          value={pairingCode}
          onChange={(e) => handlePairingCodeChange(e.target.value)}
          className="mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 font-mono tracking-widest text-gray-900 uppercase shadow-sm focus:border-primary-600 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          placeholder={t('auth.pairingCodePlaceholder')}
        />
      </div>
      {renderPrimaryCta({
        type: 'button',
        onClick: handleWizardNext,
        children: t('auth.registerContinue'),
      })}
    </div>
  );

  const renderPics = () => (
    <div className="space-y-5">
      <div className="text-center">
        <div className="mb-2 inline-flex items-center rounded-full bg-amber-100 px-3 py-1 dark:border dark:border-amber-500/30 dark:bg-amber-950/60">
          <span className="text-xs font-medium text-amber-800 dark:text-amber-100">
            {pics.versionLabel}: {picsVersion}
          </span>
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">{picsTitle}</h1>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{pics.subtitle}</p>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScrollPics}
        className="max-h-[42vh] overflow-y-auto rounded-2xl border border-gray-200 bg-gray-50 p-4 shadow-inner dark:border-gray-700 dark:bg-gray-900/50 sm:max-h-[48vh]"
        tabIndex={0}
        role="document"
        aria-label={picsTitle}
      >
        {picsDocument
          ? renderLegalMarkdown(picsDocument.content)
          : pics.sections.map((section, idx) => (
              <div key={idx} className={idx > 0 ? 'mt-4' : ''}>
                <h3 className="mb-2 text-sm font-bold text-gray-900 dark:text-white">{section.title}</h3>
                <div className="whitespace-pre-line text-xs leading-relaxed text-gray-700 dark:text-gray-300 sm:text-sm">
                  {section.content}
                </div>
              </div>
            ))}
      </div>

      {!hasScrolledToBottom ? (
        <p className="text-center text-xs text-gray-500">{t('auth.registerStepPicsScrollFirst')}</p>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <label
          className={`flex items-start gap-3 ${hasScrolledToBottom ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
          tabIndex={0}
          role="checkbox"
          aria-checked={agreed}
          aria-disabled={!hasScrolledToBottom}
          onKeyDown={(e) => {
            if (!hasScrolledToBottom) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleAgreed();
            }
          }}
        >
          <input
            type="checkbox"
            checked={agreed}
            disabled={!hasScrolledToBottom}
            onChange={handleToggleAgreed}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed"
            aria-label={pics.checkboxLabel}
          />
          <span className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{pics.checkboxLabel}</span>
        </label>
      </div>

      {renderPrimaryCta({
        type: 'button',
        onClick: handleWizardNext,
        disabled: !hasScrolledToBottom || !agreed,
        children: t('auth.registerContinue'),
      })}
    </div>
  );

  const renderReview = () => (
    <form className="space-y-5" onSubmit={handleReviewSubmit} noValidate>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth.registerStepReviewTitle')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('auth.registerStepReviewSubtitle')}</p>
      </div>

      <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
          <div>
            <p className="text-xs font-medium text-gray-500">{t('auth.registerReviewMethod')}</p>
            {authMethod === 'google' ? (
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                <span className="block">{t('auth.registerReviewMethodGoogle')}</span>
                {email.trim() ? (
                  <span className="mt-0.5 block text-xs font-normal text-gray-600 dark:text-gray-300">
                    {email.trim()}
                  </span>
                ) : null}
              </div>
            ) : (
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('auth.registerReviewMethodEmail')}
              </p>
            )}
          </div>
          <button
            type="button"
            className="shrink-0 text-sm font-semibold text-primary-700 underline dark:text-primary-400"
            onClick={() => {
              if (googlePendingToken || skipAccountMethod) {
                goToAccountMethodStep();
                return;
              }
              setPassword('');
              setConfirmPassword('');
              goToStepId('accountMethod');
            }}
          >
            {t('auth.registerReviewEdit')}
          </button>
        </div>

        <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
          <div>
            <p className="text-xs font-medium text-gray-500">{t('auth.registerReviewRole')}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedRoleLabel}</p>
          </div>
          {!pairInviteMode ? (
            <button
              type="button"
              className="shrink-0 text-sm font-semibold text-primary-700 underline dark:text-primary-400"
              onClick={() => goToStepId('role')}
            >
              {t('auth.registerReviewEdit')}
            </button>
          ) : null}
        </div>

        {authMethod === 'email' ? (
          <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
            <div>
              <p className="text-xs font-medium text-gray-500">{t('auth.registerReviewEmail')}</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{email.trim()}</p>
            </div>
            <button
              type="button"
              className="shrink-0 text-sm font-semibold text-primary-700 underline dark:text-primary-400"
              onClick={() => goToStepId('accountMethod')}
            >
              {t('auth.registerReviewEdit')}
            </button>
          </div>
        ) : null}

        <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
          <div>
            <p className="text-xs font-medium text-gray-500">{t('auth.registerReviewNickname')}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{nickname.trim()}</p>
          </div>
          <button
            type="button"
            className="shrink-0 text-sm font-semibold text-primary-700 underline dark:text-primary-400"
            onClick={() => goToStepId('nickname')}
          >
            {t('auth.registerReviewEdit')}
          </button>
        </div>

        <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
          <div>
            <p className="text-xs font-medium text-gray-500">{t('auth.registerReviewUsageRegion')}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {t(usageRegionLabelKey(usageRegion))}
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 text-sm font-semibold text-primary-700 underline dark:text-primary-400"
            onClick={() => goToStepId('nickname')}
          >
            {t('auth.registerReviewEdit')}
          </button>
        </div>

        {role === USER_ROLE.HELPER ? (
          <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
            <div>
              <p className="text-xs font-medium text-gray-500">{t('auth.registerReviewPairing')}</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {pairingCode.length > 0 ? pairingCode : t('auth.registerReviewPairingEmpty')}
              </p>
            </div>
            {!pairInviteMode ? (
              <button
                type="button"
                className="shrink-0 text-sm font-semibold text-primary-700 underline dark:text-primary-400"
                onClick={() => goToStepId('pairing')}
              >
                {t('auth.registerReviewEdit')}
              </button>
            ) : null}
          </div>
        ) : null}

        {role === USER_ROLE.EMPLOYER ? (
          <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
            <div>
              <p className="text-xs font-medium text-gray-500">{t('auth.registerReviewInvitationCode')}</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {invitationCode.length > 0
                  ? invitationCode
                  : t('auth.registerReviewInvitationCodeEmpty')}
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 text-sm font-semibold text-primary-700 underline dark:text-primary-400"
              onClick={() => goToStepId('nickname')}
            >
              {t('auth.registerReviewEdit')}
            </button>
          </div>
        ) : null}

        <div className="rounded-xl bg-primary-50 px-3 py-2 text-sm text-primary-900 dark:bg-primary-950/40 dark:text-primary-100">
          {t('auth.registerReviewPicsAgreed')}
        </div>
      </div>

      <AppButton type="submit" variant="primary" size="pill" fullWidth disabled={isSubmitting} className="shadow-sm">
        {isSubmitting ? t('auth.registering') : t('auth.registerCompleteRegistration')}
      </AppButton>
    </form>
  );

  const renderComplete = () => (
    <div className="space-y-5 text-center">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth.registerCompleteTitle')}</h1>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {authMethod === 'google' ? t('auth.registerCompleteGoogle') : t('auth.registerCompleteEmailVerify')}
      </p>
      {renderPrimaryCta({
        type: 'button',
        onClick: handleContinueFromComplete,
        children: t('auth.registerCompleteContinue'),
      })}
    </div>
  );

  const stepBody = (() => {
    switch (currentStepId) {
      case 'accountMethod':
        return renderAccountMethod();
      case 'role':
        return renderRole();
      case 'nickname':
        return renderNickname();
      case 'password':
        return renderPassword();
      case 'pairing':
        return renderPairing();
      case 'pics':
        return renderPics();
      case 'review':
        return renderReview();
      case 'complete':
        return renderComplete();
      default:
        return null;
    }
  })();

  return (
    <AuthLayout
      backTo={layoutBack}
      onBackClick={layoutOnBack}
      contentMaxWidthClassName="max-w-lg"
    >
      {renderProgress()}
      {pairInviteMode && pairInviteEmployerNickname ? (
        <p className="mb-4 rounded-xl bg-primary-50 px-3 py-2 text-sm text-primary-900 dark:bg-primary-950/40 dark:text-primary-100">
          {t('pairing.invite.registerBanner', { nickname: pairInviteEmployerNickname })}
        </p>
      ) : null}
      {error ? (
        <div
          id="register-error"
          className="mb-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-200"
          role="alert"
        >
          {error}
        </div>
      ) : null}
      {stepBody}
      <ConfirmDialog
        open={isRoleConfirmOpen}
        title={t('auth.registerRoleConfirmTitle')}
        description={t('auth.registerRoleConfirmDescription', { role: selectedRoleLabel })}
        confirmLabel={t('auth.registerCompleteRegistration')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleConfirmRegister()}
        onCancel={() => {
          if (isSubmitting) return;
          setIsRoleConfirmOpen(false);
        }}
        confirmDisabled={isSubmitting}
        cancelDisabled={isSubmitting}
        variant="default"
      />
    </AuthLayout>
  );
};
